import { GPDActivity } from '../hooks/useActivities';
import { CANtrackCase } from '../types/comment';

export type ValidatorFunction<T> = (value: T) => string | undefined;
export const validateTitle: ValidatorFunction<string> = (title) => {
	if (!title) return 'Please enter a title';
	if (title.length < 10 || title.length > 200)
		return 'Title must be between 10 and 200 characters';
	return undefined;
};

export const validateDescription: ValidatorFunction<string> = (description) => {
	if (!description) return 'Please enter a description';
	if (description.length < 2)
		return 'Description must be at least 2 characters long';
	return undefined;
};

export const validateCaseSelection: ValidatorFunction<CANtrackCase | null> = (
	selectedCase,
) => {
	if (!selectedCase) return 'Please select a case';
	return undefined;
};

export const validateActivitySelection: ValidatorFunction<
	GPDActivity | null
> = (selectedActivity) => {
	if (!selectedActivity) return 'Please select an activity';
	return undefined;
};

export const validateEntryDate: ValidatorFunction<Date | null> = (
	entryDate,
) => {
	if (!entryDate) return 'Please select an entry date';
	return undefined;
};

export const validateBillable: ValidatorFunction<boolean> = (billable) => {
	return undefined;
};

/**
 * Validation Logic Table
 *
 * | Regular Time Input | Overtime Input | Regular Time Error Message                               | Overtime Error Message                              |
 * |--------------------|----------------|----------------------------------------------------------|-----------------------------------------------------|
 * | NaN                | NaN            | "Enter a valid number for regular time"                  | "Enter a valid number for overtime"                 |
 * | NaN                | 0              | "Enter a valid number for regular time"                  | None (assuming zero is valid for overtime)          |
 * | NaN                | Not in Range   | "Enter a valid number for regular time"                  | "Enter 0 or a value between 0.01 and 24 for overtime"|
 * | 0                  | NaN            | "Enter 0 or a value between 0.01 and 24 for regular time"| "Enter a valid number for overtime"                 |
 * | 0                  | 0              | "Regular Time is required"								| None (assuming zero is valid for overtime)          |
 * | 0                  | Not in Range   | "Enter 0 or a value between 0.01 and 24 for regular time"| "Enter 0 or a value between 0.01 and 24 for overtime"|
 * | Not in Range       | NaN            | "Enter 0 or a value between 0.01 and 24 for regular time"| "Enter a valid number for overtime"                 |
 * | Not in Range       | 0              | "Enter 0 or a value between 0.01 and 24 for regular time"| None (assuming zero is valid for overtime)          |
 * | Not in Range       | Not in Range   | "Enter 0 or a value between 0.01 and 24 for regular time"| "Enter 0 or a value between 0.01 and 24 for overtime"|
 *
 * - "NaN" indicates that the input is not a number.
 * - "0" is considered a valid input for overtime, but needs validation for regular time unless explicitly allowed.
 * - "Not in Range" means the input is either negative, above 24, or not a float where expected.
 */
export function validateRegularAndOverTime(input: {
	regularTime: number;
	overTime: number;
}) {
	const { regularTime, overTime } = input;

	let regularTimeError = undefined;
	let overTimeError = undefined;

	// Check if input values are NaN
	const isRegularTimeNaN = Number.isNaN(regularTime);
	const isOverTimeNaN = Number.isNaN(overTime);

	// Check if input values are within the valid range or zero
	const isRegularTimeValid =
		(regularTime >= 0.01 && regularTime <= 24) || regularTime === 0;
	const isOverTimeValid =
		(overTime >= 0.01 && overTime <= 24) || overTime === 0;

	// Validate Overtime
	if (isOverTimeNaN) {
		overTimeError = 'Enter a valid number for overtime';
	} else if (!isOverTimeValid) {
		overTimeError = 'Enter 0 or a value between 0.01 and 24 for overtime';
	}

	// Validate Regular Time
	if (isRegularTimeNaN) {
		regularTimeError = 'Enter a valid number for regular time';
	} else if (!isRegularTimeValid) {
		// Check if zero for regular time is valid only if overtime is also valid
		if (regularTime === 0 && !overTimeError) {
			regularTimeError = undefined; // Zero is valid for regular time when overtime is also valid
		} else {
			regularTimeError =
				'Enter 0 or a value between 0.01 and 24 for regular time';
		}
	}

	// Construct error object
	const errors = {
		regularTime: regularTimeError,
		overTime: overTimeError,
	};

	// Return error object if there are any errors, otherwise return undefined
	return regularTimeError || overTimeError
		? JSON.stringify(errors)
		: undefined;
}
